import { useEffect, useRef, useState } from "react";
import { Chip, Input } from "@nextui-org/react";
import { DropArrowIcon } from "@/assets/icons/admin";

interface CustomDropdownProps {
  options: string[];
  selectedPrivileges?: string[];
  setSelectedPrivileges?: React.Dispatch<
    React.SetStateAction<string[] | undefined>
  >;
  placeholder?: string;
  onChipViewHeightChange?: (height: number) => void;
  enableFilter?: boolean;
}

const CustomChipDropdown: React.FC<CustomDropdownProps> = ({
  options,
  selectedPrivileges,
  setSelectedPrivileges,
  placeholder = "Select Page Access",
  onChipViewHeightChange,
  enableFilter = false,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    selectedPrivileges ?? [],
  );
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState("");

  const chipViewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedOptions(selectedPrivileges ?? []);
  }, [selectedPrivileges]);

  useEffect(() => {
    if (chipViewRef.current && onChipViewHeightChange) {
      onChipViewHeightChange(chipViewRef.current.offsetHeight);
    }
  }, [selectedOptions, onChipViewHeightChange]);

  const toggleOption = (option: string) => {
    const updated = selectedOptions.includes(option)
      ? selectedOptions.filter((item) => item !== option)
      : [...selectedOptions, option];

    setSelectedOptions(updated);
    setSelectedPrivileges?.(updated);
  };

  const filteredOptions = enableFilter
    ? options.filter((option) =>
        option.toLowerCase().includes(filter.toLowerCase()),
      )
    : options;

  return (
    <div className="relative">
      <div
        className="input-bg bg-white-5 shadow-xl h-10 w-full cursor-pointer flex-row items-center rounded-lg border border-solid border-white border-opacity-50 p-2 pr-8 backdrop-blur-xl"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-sm text-gray-500">{placeholder}</span>
        <span className="float-right mt-2">
          <DropArrowIcon />
        </span>
      </div>

      {selectedOptions.length > 0 && (
        <div
          ref={chipViewRef}
          className="input-bg absolute z-40 mt-1 flex w-full flex-wrap rounded-lg border border-gray-200 bg-white py-2"
        >
          {selectedOptions.map((option) => (
            <div
              key={option}
              className="m-1"
              onClick={() => toggleOption(option)}
            >
              <Chip
                className="whitelist-chip no-hover"
                onClose={() => toggleOption(option)}
              >
                {option}
              </Chip>
            </div>
          ))}
        </div>
      )}

      {isOpen && (
        <div
          className={`shadow-lg absolute z-40 w-full rounded-md bg-white ${
            selectedOptions.length > 0 ? "mt-16" : "mt-0"
          }`}
        >
          {/* 🔍 Filter input if enabled */}
          {enableFilter && (
            <div className="border-b border-gray-200 px-3 py-2">
              <Input
                size="sm"
                placeholder="Search..."
                className="text-sm text-black"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
          )}

          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option}
                  className={`cursor-pointer px-4 py-2 text-black hover:bg-gray-100 ${
                    selectedOptions.includes(option) ? "opacity-50" : ""
                  }`}
                  onClick={() => toggleOption(option)}
                >
                  {option}
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-400">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomChipDropdown;
